import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Select from 'react-select';
import withApi from '../higherOrderComponents/withApi';
import { useSortable } from '../../common/useSortable';
import { useFacilityProducts } from '../../common/useFacilityProducts';
import { naturalCompare } from '../../common/utils';
import { SortHandle, MoveButtons, sortableRowStyle, rowActionStyle } from '../misc/SortHandle';

/**
 * The "New view" of a resource group's splits: the same tables as the classic
 * view, in the same style, with what they are missing.
 *
 * - Resources inside a split can be dragged (or moved with ↑ ↓) into order; the
 *   order is saved and is display only. Resources nobody has ordered come sorted
 *   by name, numbers read as numbers ("Bane 2" before "Bane 10").
 * - A resource without a product says so, instead of showing an empty cell.
 * - Products are picked by name among the facility's own: names repeat across
 *   facilities, so only those can be told apart.
 * - "add" opens a row already named after the group and sold through the split's
 *   last product: resources in one split are parts of the same space, usually
 *   sold the same way (a hall with four badminton courts).
 * - "Add split" copies the names of another split, to be adjusted before saving,
 *   and asks for the new split's product: the split is created complete.
 * - Removing asks first, like the classic view does.
 */

const HEADER_STYLE = { backgroundColor: '#5e5e5e', color: '#f1f1f3' };
const ACTION_STYLE = { cursor: 'pointer', marginRight: 10 };
const HELP_STYLE = { color: 'grey', fontSize: '0.85rem' };
const SELECT_PROPS = {
  className: 'react-select-filters-section',
  classNamePrefix: 'react-select-filters-section-prefix'
};

const byDisplayOrderThenName = (a, b) =>
  (a.displayOrder ?? Infinity) - (b.displayOrder ?? Infinity) || naturalCompare(a.name, b.name);

const sortedResources = split => [...(split.resources || [])].sort(byDisplayOrderThenName);

const productOption = resource => (resource?.productId
  ? { value: resource.productId, label: resource.product?.productName || resource.productId }
  : null);

const escapeRegExp = text => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

function SplitTable({ api, split, index, groupName, products, busy, editing, setEditing, run, onReorder }) {
  const resources = sortedResources(split);
  const sortable = useSortable({
    items: resources,
    getId: resource => resource.id,
    onReorder: orderedIds => onReorder(split, orderedIds),
    enabled: !busy && !editing && resources.length > 1
  });
  // A resource being added to this split: `editing` with no id.
  const adding = editing && !editing.id && editing.splitId === split.id;

  const removeSplit = () => {
    if (!confirm('Are you sure to delete this split? Its resources go with it.')) return;
    run(() => api.resources.removeSplit(split.id));
  };

  const removeResource = resource => {
    if (!confirm(`Are you sure to delete ${resource.name || 'this resource'}?`)) return;
    run(() => api.resources.removeResource(resource.id));
  };

  const saveResource = () => run(async () => {
    if (!editing.name.trim() || !editing.product) throw new Error('A resource needs a name and a product.');

    if (editing.id) {
      await api.resources.saveResource({
        Id: editing.id,
        name: editing.name,
        productId: editing.product.value,
        productName: editing.product.label,
        resourceSplitId: split.id
      });
    } else {
      await api.resources.addResourceWithProduct(split.id, editing.name.trim(), editing.product.value);
    }
    setEditing(null);
  });

  const nameInput = (
    <input
      value={editing?.name || ''}
      onChange={({ target }) => setEditing({ ...editing, name: target.value })}
    />
  );

  const productSelect = (
    <Select
      {...SELECT_PROPS}
      isLoading={!products}
      options={products || []}
      value={editing?.product || null}
      onChange={selection => setEditing({ ...editing, product: selection })}
      placeholder="Product of this centre"
    />
  );

  const saveCancel = (
    <>
      <span style={rowActionStyle()} onClick={() => setEditing(null)}>cancel</span>
      <span style={rowActionStyle()} onClick={() => !busy && saveResource()}>save</span>
    </>
  );

  return (
    <>
      <span>Split {index + 1} - {split.id}</span>
      <span style={{ float: 'right', cursor: 'pointer' }} onClick={() => !busy && removeSplit()}>remove</span>

      <table className="data-lists component-data-table cell-content-align-top data-table-no-results">
        <tbody>
          <tr style={HEADER_STYLE}>
            <td>#</td>
            <td>Id</td>
            <td>Name</td>
            <td>Product</td>
            <td />
          </tr>
          {resources.map((resource, position) => {
            const isEditing = editing && editing.id === resource.id;
            const productName = resource.product?.productName;

            return (
              <tr
                key={resource.id}
                style={{ ...sortableRowStyle(sortable, resource), ...(isEditing ? { backgroundColor: '#cbcbcb' } : {}) }}
                {...sortable.rowProps(resource)}
              >
                <td><SortHandle sortable={sortable} item={resource} position={position + 1} /></td>
                <td>{resource.id}</td>
                <td>{isEditing ? nameInput : resource.name}</td>
                <td>
                  {isEditing
                    ? productSelect
                    : (productName || <span style={{ color: '#b9770e' }}>No product</span>)}
                </td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  {isEditing ? saveCancel : (
                    <>
                      <MoveButtons sortable={sortable} item={resource} index={position} count={resources.length} />
                      <span
                        style={rowActionStyle()}
                        onClick={() => !busy && setEditing({
                          id: resource.id,
                          name: resource.name || '',
                          product: productOption(resource)
                        })}
                      >
                        edit
                      </span>
                      <span style={rowActionStyle(true)} onClick={() => !busy && removeResource(resource)}>remove</span>
                    </>
                  )}
                </td>
              </tr>
            );
          })}
          {adding ? (
            <tr style={{ backgroundColor: '#cbcbcb' }}>
              <td>{resources.length + 1}</td>
              <td>new</td>
              <td>{nameInput}</td>
              <td>{productSelect}</td>
              <td style={{ whiteSpace: 'nowrap' }}>{saveCancel}</td>
            </tr>
          ) : (
            <tr>
              <td colSpan={5}>
                <span
                  style={ACTION_STYLE}
                  onClick={() => !busy && setEditing({
                    id: null,
                    splitId: split.id,
                    name: groupName || '',
                    product: productOption(resources[resources.length - 1])
                  })}
                >
                  add
                </span>
              </td>
            </tr>
          )}
        </tbody>
      </table>
      <br />
    </>
  );
}

SplitTable.propTypes = {
  api: PropTypes.object.isRequired,
  split: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  groupName: PropTypes.string,
  products: PropTypes.arrayOf(PropTypes.object),
  busy: PropTypes.bool.isRequired,
  editing: PropTypes.object,
  setEditing: PropTypes.func.isRequired,
  run: PropTypes.func.isRequired,
  onReorder: PropTypes.func.isRequired
};

// The new split's working copy: the names of the split it copies, editable, and
// the product all of them will be sold through. A group without resources yet
// starts from one resource named after the group.
const draftFrom = (splits, source, groupName) => {
  const names = source === null ? [] : sortedResources(splits[source]).map(resource => resource.name || '');
  return { source, names: names.length ? names : [groupName || ''], find: '', replace: '', product: null };
};

function NewSplitForm({ splits, groupName, products, draft, setDraft, busy, onAdd, onCancel }) {
  const replaceAll = () => {
    if (!draft.find) return;
    const pattern = new RegExp(escapeRegExp(draft.find), 'gi');
    setDraft({ ...draft, names: draft.names.map(name => name.replace(pattern, draft.replace)) });
  };

  return (
    <div style={{ border: '1px solid #d6d6d6', padding: 16, marginBottom: 16 }}>
      <strong>New split</strong>
      <br />
      <br />
      {splits.length > 0 && (
        <>
          <label htmlFor="new-split-source">Copy the resources of</label>
          <select
            id="new-split-source"
            value={draft.source}
            onChange={({ target }) => setDraft(draftFrom(splits, Number(target.value), groupName))}
          >
            {splits.map((split, index) => (
              <option key={split.id} value={index}>
                Split {index + 1} ({(split.resources || []).length} resources)
              </option>
            ))}
          </select>
          <br />
        </>
      )}
      <label htmlFor="new-split-find">Replace in all names</label>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
        <input
          id="new-split-find"
          style={{ width: 220 }}
          value={draft.find}
          placeholder="e.g. Badmintonbane"
          onChange={({ target }) => setDraft({ ...draft, find: target.value })}
        />
        <span>with</span>
        <input
          style={{ width: 220 }}
          aria-label="Replace with"
          value={draft.replace}
          placeholder="e.g. Pickleballbane"
          onChange={({ target }) => setDraft({ ...draft, replace: target.value })}
        />
        <button type="button" className="button button-color-secondary" style={{ padding: '4px 12px' }} onClick={replaceAll}>
          Replace all
        </button>
      </div>
      <br />
      <span>Names of the new resources</span>
      {draft.names.map((name, index) => (
        <input
          // Positions do not move while the form is open.
          // eslint-disable-next-line react/no-array-index-key
          key={index}
          aria-label={`Name ${index + 1}`}
          value={name}
          onChange={({ target }) => setDraft({
            ...draft,
            names: draft.names.map((current, at) => (at === index ? target.value : current))
          })}
        />
      ))}
      <span style={HELP_STYLE}>Copied from the split above: adjust them here before adding.</span>
      <br />
      <br />
      <label htmlFor="new-split-product">Product for the new split</label>
      <Select
        {...SELECT_PROPS}
        inputId="new-split-product"
        isLoading={!products}
        options={products || []}
        value={draft.product}
        onChange={selection => setDraft({ ...draft, product: selection })}
        placeholder="Product of this centre"
      />
      <span style={HELP_STYLE}>
        Every resource of the new split is sold through it. None is chosen for you: a new split is
        there to sell the space another way.
      </span>
      <br />
      <br />
      <button
        type="button"
        className="button button-color-secondary button-shrink-limited"
        style={{ marginRight: 10 }}
        onClick={onCancel}
      >
        Cancel
      </button>
      <button
        type="button"
        className="button button-color-primary button-shrink-limited"
        disabled={busy}
        onClick={onAdd}
      >
        Add split
      </button>
    </div>
  );
}

NewSplitForm.propTypes = {
  splits: PropTypes.arrayOf(PropTypes.object).isRequired,
  groupName: PropTypes.string,
  products: PropTypes.arrayOf(PropTypes.object),
  draft: PropTypes.object.isRequired,
  setDraft: PropTypes.func.isRequired,
  busy: PropTypes.bool.isRequired,
  onAdd: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired
};

const ResourceSplitsBoard = ({ api, groupId, groupName, facilityId, splits, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);
  // One resource at a time: { id (null when adding), splitId, name, product: { value, label } }.
  const [editing, setEditing] = useState(null);
  // The split being added, see `draftFrom`.
  const [splitDraft, setSplitDraft] = useState(null);
  // Orders saved in this view, shown at once while the group reloads.
  const [orders, setOrders] = useState({});
  const products = useFacilityProducts(api, facilityId);

  const run = async fn => {
    setBusy(true);
    setMessage(null);
    try {
      await fn();
      await onChanged();
    } catch (error) {
      setMessage(error?.message || 'unknown error');
    } finally {
      setBusy(false);
    }
  };

  const reorder = async (split, orderedIds) => {
    setOrders({ ...orders, [split.id]: orderedIds });
    try {
      await api.resources.reorderResources(split.id, orderedIds);
      await onChanged();
    } catch (error) {
      setMessage(`Unable to save the order: ${error?.message || 'unknown error'}`);
    } finally {
      setOrders(current => {
        const { [split.id]: done, ...rest } = current;
        return rest;
      });
    }
  };

  const addSplit = () => run(async () => {
    const names = splitDraft.names.map(name => name.trim());
    if (names.some(name => !name)) throw new Error('Every resource of the new split needs a name.');
    if (!splitDraft.product) throw new Error('Choose the product of the new split.');

    await api.resources.addSplitWithResources(
      groupId,
      names.map(name => ({ name, productId: splitDraft.product.value }))
    );
    setSplitDraft(null);
  });

  // A split whose new order is still being saved shows it already.
  const shown = splits.map(split => (!orders[split.id] ? split : {
    ...split,
    resources: orders[split.id].map((id, position) => ({
      ...split.resources.find(resource => resource.id === id),
      displayOrder: position
    }))
  }));

  return (
    <>
      {message && <p style={{ color: '#c0392b' }}>{message}</p>}
      <p style={HELP_STYLE}>
        Drag ⠿ or use ↑ ↓ to reorder the resources of a split: the order is saved for everyone
        and only changes how the list looks.
      </p>
      {shown.map((split, index) => (
        <SplitTable
          key={split.id}
          api={api}
          split={split}
          index={index}
          groupName={groupName}
          products={products}
          busy={busy}
          editing={editing}
          setEditing={value => { setSplitDraft(null); setEditing(value); }}
          run={run}
          onReorder={reorder}
        />
      ))}
      {splitDraft ? (
        <NewSplitForm
          splits={shown}
          groupName={groupName}
          products={products}
          draft={splitDraft}
          setDraft={setSplitDraft}
          busy={busy}
          onAdd={addSplit}
          onCancel={() => setSplitDraft(null)}
        />
      ) : (
        <span
          style={ACTION_STYLE}
          onClick={() => {
            if (busy) return;
            setEditing(null);
            setSplitDraft(draftFrom(shown, shown.length ? shown.length - 1 : null, groupName));
          }}
        >
          Add split
        </span>
      )}
      <br />
      <br />
    </>
  );
};

ResourceSplitsBoard.propTypes = {
  api: PropTypes.object.isRequired,
  groupId: PropTypes.string.isRequired,
  groupName: PropTypes.string,
  facilityId: PropTypes.string,
  splits: PropTypes.arrayOf(PropTypes.object).isRequired,
  onChanged: PropTypes.func.isRequired
};

export default withApi(ResourceSplitsBoard);
